import { Injectable } from '@angular/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  IFormDocument,
  IListElement,
  IListOptionList,
} from 'app/services/api5-service/api.interface';
import { IListElementCustomHeader } from 'app/services/api5-service/api.interface/list-element-custom-header.interface';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, pluck } from 'rxjs/operators';

import { DocHelper } from 'utils/dochelper';

import { FormService } from './form.service';
import { SetValuesService } from './set-values.service';

import { IListMap } from '../dynamic-form.interface';
import { IListRowUi } from '../types/list/model';

/** Key - target property, value - tags */
type ListFilterOption = Record<string, string[]>;

/**
 * Service for list field
 */
@UntilDestroy()
@Injectable()
export class ListService {
  private readonly listMapSubject: BehaviorSubject<IListMap> =
    new BehaviorSubject({});

  /** Flag to correctly reset filters */
  private prevFlistCame = false;

  private readonly filterSubject: BehaviorSubject<
    Record<string, ListFilterOption>
  > = new BehaviorSubject({});

  private readonly filterResetSubject: Subject<void> = new Subject();

  readonly filterReset$ = this.filterResetSubject.asObservable();

  constructor(
    private readonly setValuesService: SetValuesService,
    private readonly formService: FormService,
  ) {
    this.subscribeToDocChange();
    this.subscribeToSetValuesChanges();
  }

  private resetFiltersIfNeeded(doc: IFormDocument): void {
    const needToResetFilters = this.prevFlistCame && doc.flist !== undefined;
    if (needToResetFilters) {
      this.filterResetSubject.next();
    }
    this.prevFlistCame = doc.flist !== undefined;
  }

  /**
   * Watch for doc lists
   */
  private subscribeToDocChange(): void {
    this.formService.doc$.pipe(untilDestroyed(this)).subscribe(doc => {
      this.resetFiltersIfNeeded(doc);
      this.addElements(doc.list);
    });
  }

  /**
   * Lists can be changed during set values. Subscribe to this event
   */
  private subscribeToSetValuesChanges(): void {
    this.setValuesService.setValuesRequestEvent$
      .pipe(untilDestroyed(this))
      .subscribe(event => {
        if ('result' in event && event.result.setValuesDoc.list) {
          this.addElements(event.result.setValuesDoc.list);
          this.resetFiltersIfNeeded(event.result.setValuesDoc);
        }
      });
  }

  private isFilterOptionApplied(value: string[]): boolean {
    return value.some(v => Boolean(v));
  }

  isSomeFilterApplied$(listName: string): Observable<boolean> {
    return this.filterSubject.pipe(
      map(filterModel => {
        const listFilterModel = filterModel[listName];
        if (!listFilterModel) {
          return false;
        }
        return Object.values(listFilterModel).some(value =>
          this.isFilterOptionApplied(value),
        );
      }),
    );
  }

  isListWithFilter(listName: string): boolean {
    return Boolean(this.filterSubject.value[listName]);
  }

  isListWithFilter$(listName: string): Observable<boolean> {
    return this.filterSubject.pipe(
      map(filter => Boolean(filter[listName])),
      distinctUntilChanged(),
    );
  }

  resetFilter(): void {
    this.filterResetSubject.next();
  }

  setFilter(listName: string, targetProperty: string, tags: string[]): void {
    this.filterSubject.next({
      ...this.filterSubject.value,
      [listName]: {
        ...this.filterSubject.value[listName],
        [targetProperty]: tags.filter(tag => Boolean(tag) && tag !== 'null'),
        // BE can send tags with empty values, so this values won't affect any filtering
        // some kind of 'reset filter'. Used for switcher filters
      },
    });
  }

  getFilteredRows$(
    listName: string,
    rows: IListRowUi[],
  ): Observable<IListRowUi[]> {
    return this.filterSubject.pipe(
      pluck(listName),
      map(filterForList => {
        if (!filterForList) {
          return rows;
        }

        const filtersToApply = Object.entries(filterForList).filter(
          ([_, tags]) => tags.length,
        );
        if (!filtersToApply.length) {
          return rows;
        }

        return rows.filter(row => {
          const originalElement = row.originalElement;
          if (!originalElement) {
            return true;
          }

          return filtersToApply.every(([targetProperty, tags]) => {
            if (!(targetProperty in originalElement)) {
              // if elem have no target property then... let's hope it will have this property
              return false;
            }

            const elemTargetCell = originalElement[targetProperty];
            if (!('tag' in elemTargetCell)) {
              // elem have no tags, then it will be filtered;
              return false;
            }

            const elemTags = DocHelper.getArrayWrapperValue(elemTargetCell.tag);
            return tags.every(tag => elemTags.some(t => t.$ === tag));
          });
        });
      }),
    );
  }

  getListElements$(listName: string): Observable<IListElement[]> {
    return this.listMapSubject.pipe(
      map(listMap => listMap[listName]?.elem || []),
    );
  }

  getListElems(name: string): IListElement[] | undefined {
    return this.listMapSubject.value[name]?.elem;
  }

  getListCustomHeaders(name: string): IListElementCustomHeader[] | undefined {
    return this.listMapSubject.value[name]?.header;
  }

  addElements(list?: IListOptionList[]): void {
    if (!list) {
      return;
    }

    this.listMapSubject.next({
      ...this.listMapSubject.value,
      ...DocHelper.reduceFormList(list),
    });
  }
}
